import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  Divider,
  Stack,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { canAccessKdsMonitor } from '../utils/auth';
import {
  buildCatalogFromProducts,
  fetchInventoryProductsWithOverrides,
  readSharedProductCatalog,
  getPreparationDescriptionLines,
  normalizeKdsName,
  normalizePreparationDescription,
  persistSharedCatalogData,
  resolveCatalogImageUrl,
} from '../utils/catalog';
import { isSalonKdsOrder, shouldShowKdsRoute } from '../utils/kdsOrders';
import {
  clearKdsDeviceSession,
  fetchKdsDeviceSession,
  getKdsDeviceSession,
  kdsDeviceFetch,
} from '../utils/kdsDeviceApi';

const API_URL = getApiUrl();
const KDS_RECONCILE_INTERVAL_MS = 15000;
const KDS_RETRY_MAX_MS = 10000;

const ensureShowInKds = (value) => value !== false;

const statusLabels = {
  preparing: 'Em preparo',
  ready: 'Pronto',
  enRoute: 'Em rota',
  finished: 'Concluído',
};

const buildOrderChip = (order) => {
  if (order.source === '99food' || String(order.orderId || '').startsWith('99FOOD-')) {
    return <Chip label="99Food" color="warning" size="small" variant="outlined" />;
  }
  if (order.source === 'salao' || String(order.orderId || '').startsWith('SALAO-')) {
    return <Chip label="Salão" color="error" size="small" variant="filled" />;
  }
  if (order.source === 'balcao' || String(order.orderId || '').startsWith('BALCAO-')) {
    return <Chip label="Balcão" color="secondary" size="small" variant="outlined" />;
  }
  if (order.source === 'site' || String(order.orderId || '').startsWith('SITE-')) {
    return <Chip label="Site" color="primary" size="small" variant="outlined" />;
  }
  return <Chip label={order.salesChannel || 'iFood'} color="primary" size="small" variant="outlined" />;
};

const isSalonOrder = isSalonKdsOrder;

const getKdsSentAt = (order = {}) => (
  order.additionalInfo?.kds?.sentAt ||
  order.sentAt ||
  order.updatedAt ||
  order.createdAt ||
  ''
);

const getKdsSequence = (order = {}) => {
  const value = order.routeNumber ?? order.routeSequence ?? order.kdsSequence ?? order.additionalInfo?.kds?.routeNumber ?? order.additionalInfo?.kds?.routeSequence ?? order.additionalInfo?.kds?.kdsSequence;
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : Number.MAX_SAFE_INTEGER;
};

const getKdsRouteLabel = (order = {}) => {
  const sequence = getKdsSequence(order);
  return sequence === Number.MAX_SAFE_INTEGER ? 'Rota' : `Rota ${sequence}`;
};

const getKdsSelectionOrder = (order = {}) => {
  const value = order.selectionOrder ?? order.additionalInfo?.kds?.selectionOrder;
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : Number.MAX_SAFE_INTEGER;
};

const wasMarkedReadyByKds = (order = {}) => (
  order?.readySource === 'kds-monitor' ||
  order?.additionalInfo?.kds?.readySource === 'kds-monitor' ||
  order?.additionalInfo?.kds?.kitchenReady === true
);

const compareKdsOrders = (a, b) => {
  const sequenceDiff = getKdsSequence(a) - getKdsSequence(b);
  if (sequenceDiff !== 0) return sequenceDiff;
  const aTime = new Date(getKdsSentAt(a) || 0).getTime();
  const bTime = new Date(getKdsSentAt(b) || 0).getTime();
  const safeATime = Number.isFinite(aTime) ? aTime : 0;
  const safeBTime = Number.isFinite(bTime) ? bTime : 0;
  if (safeATime !== safeBTime) return safeATime - safeBTime;
  const selectionDiff = getKdsSelectionOrder(a) - getKdsSelectionOrder(b);
  if (selectionDiff !== 0) return selectionDiff;
  return String(a.displayId || a.orderId || '').localeCompare(String(b.displayId || b.orderId || ''));
};

const getOrderMerchantId = (order = {}) => String(
  order?.merchant?.id ||
  order?.merchant?.merchantId ||
  order?.fullOrderData?.merchant?.id ||
  order?.fullOrderData?.merchantId ||
  order?.lastPayload?.merchantId ||
  order?.merchantId ||
  ''
).trim();

const KdsMonitor = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [accessMode, setAccessMode] = useState('checking');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchGenerationRef = useRef(0);
  const initialLoadRef = useRef(null);
  const catalogRef = useRef({ products: {}, options: {} });
  const catalogLoadedAtRef = useRef(0);
  const [imagePreview, setImagePreview] = useState({ open: false, url: '', title: '' });
  const [markingReady, setMarkingReady] = useState({});

  useEffect(() => {
    if (auth.loading) return undefined;

    if (getKdsDeviceSession()) {
      let active = true;
      const validateDeviceSession = async () => {
        try {
          await fetchKdsDeviceSession();
          if (active) setAccessMode('device');
        } catch {
          clearKdsDeviceSession();
          if (active) navigate('/kds/acesso', { replace: true });
        }
      };

      validateDeviceSession();
      return () => {
        active = false;
      };
    }

    if (auth.isAuthenticated && canAccessKdsMonitor(auth.user)) {
      setAccessMode('primary');
      return undefined;
    }

    navigate('/kds/acesso', { replace: true });
    return undefined;
  }, [auth.isAuthenticated, auth.loading, auth.user, navigate]);

  const loadCatalog = useCallback(async () => {
    if (accessMode === 'checking') return catalogRef.current;
    if (catalogLoadedAtRef.current && Date.now() - catalogLoadedAtRef.current < 60000) {
      return catalogRef.current;
    }

    try {
      if (accessMode === 'device') {
        const response = await kdsDeviceFetch('/api/kds/device/catalog');
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false || !data.catalog) {
          if (response.status === 401) {
            navigate('/kds/acesso', { replace: true });
          }
          throw new Error('Falha ao carregar o catálogo do KDS.');
        }
        const catalog = {
          products: data.catalog.products || {},
          options: data.catalog.options || {},
        };
        catalogRef.current = catalog;
        catalogLoadedAtRef.current = Date.now();
        return catalog;
      }

      const items = await fetchInventoryProductsWithOverrides(API_URL);
      const normalized = buildCatalogFromProducts(items);
      persistSharedCatalogData(items);
      catalogRef.current = normalized;
      catalogLoadedAtRef.current = Date.now();
      return normalized;
    } catch (err) {
      console.error('Falha ao carregar catálogo persistido do estoque', err);
      if (accessMode === 'device') throw err;
      try {
        const stored = readSharedProductCatalog();
        const fallback = {
          products: stored.products || {},
          options: stored.options || {},
        };
        catalogRef.current = fallback;
        catalogLoadedAtRef.current = Date.now();
        return fallback;
      } catch {
        const fallback = { products: {}, options: {} };
        catalogRef.current = fallback;
        catalogLoadedAtRef.current = Date.now();
        return fallback;
      }
    }
  }, [accessMode, navigate]);

  const applyCatalogToItem = useCallback((item, currentCatalog, orderMerchantId = '') => {
    const catalog = currentCatalog || catalogRef.current;
    const productKey = orderMerchantId && item.externalCode ? `${orderMerchantId}::${item.externalCode}` : '';
    const matchedProduct = item.externalCode ? (catalog.products?.[productKey] || catalog.products?.[item.externalCode]) : null;
    const enrichedOptions = (item.options || []).map((option) => {
      const optionKey = orderMerchantId && option.externalCode ? `${orderMerchantId}::${option.externalCode}` : '';
      const matchedOption = option.externalCode ? (catalog.options?.[optionKey] || catalog.options?.[option.externalCode]) : null;
      return {
        ...option,
        name: matchedOption?.name || option.name,
        description: matchedOption?.description ?? option.description,
        imageUrl: matchedOption?.imageUrl || option.imageUrl,
        showInKds: ensureShowInKds(matchedOption?.showInKds ?? option.showInKds),
      };
    });

    return {
      ...item,
      name: normalizeKdsName(matchedProduct?.kdsName) || matchedProduct?.name || item.name,
      description: matchedProduct?.description ?? item.description,
      imageUrl: matchedProduct?.imageUrl || item.imageUrl,
      preparationDescription: normalizePreparationDescription(
        matchedProduct?.preparationDescription ?? item.preparationDescription
      ),
      showInKds: ensureShowInKds(matchedProduct?.showInKds ?? item.showInKds),
      options: enrichedOptions,
    };
  }, []);

  const applyCatalogToOrders = useCallback((list, currentCatalog) => {
    return (list || []).map((order) => ({
      ...order,
      items: order.items?.map((item) => applyCatalogToItem(item, currentCatalog, getOrderMerchantId(order))) || [],
    }));
  }, [applyCatalogToItem]);

  const fetchOrders = useCallback(async (withLoader = false, syncIfood = false) => {
    if (accessMode === 'checking') return;
    const generation = ++fetchGenerationRef.current;
    if (withLoader) {
      setLoading(true);
    }
    try {
      const catalog = await loadCatalog();
      const syncParam = syncIfood ? '?sync=1' : '';
      const res = accessMode === 'device'
        ? await kdsDeviceFetch('/api/kds/device/orders')
        : await apiFetch(`${API_URL}/api/kds/orders${syncParam}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        if (accessMode === 'device' && res.status === 401) {
          navigate('/kds/acesso', { replace: true });
          return;
        }
        throw new Error(data.message || data.error || 'Falha ao buscar pedidos do KDS');
      }

      const withCatalog = applyCatalogToOrders(data.orders || [], catalog);
      const sanitized = withCatalog.map((order) => ({
        ...order,
        items: (order.items || [])
          .map((item) => {
            const visibleOptions = (item.options || [])
              .map((opt) => ({ ...opt, showInKds: ensureShowInKds(opt.showInKds) }))
              .filter((opt) => opt.showInKds);
            return {
              ...item,
              showInKds: ensureShowInKds(item.showInKds),
              options: visibleOptions,
            };
          })
          .filter((item) => item.showInKds),
      })).filter((order) => (order.items || []).length > 0);

      if (generation !== fetchGenerationRef.current) return;
      setOrders(sanitized);
      setError(null);
    } catch (err) {
      if (generation !== fetchGenerationRef.current) return;
      console.error('Erro no KDS', err);
      setError(err.message);
    } finally {
      if (generation === fetchGenerationRef.current) setLoading(false);
    }
  }, [accessMode, applyCatalogToOrders, loadCatalog, navigate]);

  useEffect(() => {
    if (accessMode !== 'checking') {
      initialLoadRef.current = fetchOrders(true, accessMode === 'primary');
    }
    return () => { fetchGenerationRef.current += 1; };
  }, [accessMode, fetchOrders]);

  useEffect(() => {
    if (accessMode === 'checking') return undefined;
    const refresh = () => fetchOrders(false, false);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const interval = setInterval(refresh, KDS_RECONCILE_INTERVAL_MS);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [accessMode, fetchOrders]);

  useEffect(() => {
    if (accessMode === 'checking') return undefined;
    let active = true;
    let controller = null;
    let retryTimer = null;
    let retryResolve = null;
    let cursor = '';
    let failures = 0;
    const waitBeforeRetry = (delay) => new Promise((resolve) => {
      retryResolve = resolve;
      retryTimer = setTimeout(resolve, delay);
    });

    const watchChanges = async () => {
      while (active) {
        controller = new AbortController();
        const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
        const path = accessMode === 'device'
          ? `/api/kds/device/changes${query}`
          : `${API_URL}/api/kds/orders/changes${query}`;
        try {
          const response = accessMode === 'device'
            ? await kdsDeviceFetch(path, { signal: controller.signal })
            : await apiFetch(path, { signal: controller.signal });
          if (!active) return;
          if (response.status === 401) {
            if (accessMode === 'device') navigate('/kds/acesso', { replace: true });
            return;
          }
          const data = await response.json().catch(() => ({}));
          if (!response.ok || data.success === false || !data.cursor) {
            throw new Error('Falha ao acompanhar mudanças do KDS.');
          }
          const firstObservation = !cursor;
          cursor = String(data.cursor);
          failures = 0;
          if (firstObservation) {
            // Let the initial admin sync finish before the cursor gap check.
            // Otherwise this read can discard its result as an older generation.
            await initialLoadRef.current;
            if (!active) return;
          }
          if (firstObservation || data.changed) await fetchOrders(false, false);
        } catch (err) {
          if (!active || err.name === 'AbortError') return;
          failures += 1;
          await waitBeforeRetry(Math.min(1000 * (2 ** (failures - 1)), KDS_RETRY_MAX_MS));
          retryResolve = null;
          retryTimer = null;
          if (active) await fetchOrders(false, false);
        }
      }
    };

    watchChanges();
    return () => {
      active = false;
      controller?.abort();
      if (retryTimer) clearTimeout(retryTimer);
      retryResolve?.();
    };
  }, [accessMode, fetchOrders, navigate]);

  const preparingOrders = useMemo(() => {
    return orders
      .sort(compareKdsOrders);
  }, [orders]);

  const changeOrderStatus = useCallback(async (orderId, newStatus) => {
    setMarkingReady((prev) => ({ ...prev, [orderId]: true }));
    try {
      if (newStatus !== 'ready') return;
      const path = accessMode === 'device'
        ? `/api/kds/device/orders/${encodeURIComponent(orderId)}/ready`
        : `${API_URL}/api/kds/orders/${encodeURIComponent(orderId)}/ready`;
      const request = {
        method: 'POST',
        body: JSON.stringify({ expectedRevision: orders.find((order) => String(order.orderId) === String(orderId))?.amendmentRevision || 0 }),
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const response = accessMode === 'device'
        ? await kdsDeviceFetch(path, request)
        : await apiFetch(path, request);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        if (accessMode === 'device' && response.status === 401) {
          navigate('/kds/acesso', { replace: true });
          return;
        }
        throw new Error(data.message || data.error || 'Falha ao marcar pedido como pronto pelo KDS.');
      }

      setOrders((prev) => prev.filter((order) => (
        String(order.orderId || order.id || '') !== String(orderId)
      )));
      fetchGenerationRef.current += 1;
    } catch (err) {
      setError(`Erro ao alterar status: ${err.message}`);
    } finally {
      setMarkingReady((prev) => {
        const copy = { ...prev };
        delete copy[orderId];
        return copy;
      });
    }
  }, [accessMode, navigate, orders]);

  const changeKitchen = () => {
    clearKdsDeviceSession();
    navigate('/kds/acesso', { replace: true });
  };

  if (accessMode === 'checking') {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: '#f8fafc' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress aria-label="Verificando acesso ao KDS" />
          <Typography color="text.secondary">Verificando acesso ao KDS…</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      p: 3,
      background: '#f8fafc',
      color: '#0f172a'
    }}>
      {accessMode === 'device' ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="overline" color="text.secondary">KDS conectado</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {getKdsDeviceSession()?.company?.name || 'Cozinha'}
            </Typography>
          </Box>
          <Button variant="outlined" color="inherit" onClick={changeKitchen}>
            Trocar cozinha
          </Button>
        </Stack>
      ) : null}
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Paper sx={{ p: 2, mb: 2, background: '#fff1f2', color: '#7f1d1d', border: '1px solid #fecdd3' }}>
          <Typography color="inherit">Falha ao sincronizar: {error}</Typography>
        </Paper>
      )}

      <Grid container spacing={2}>
        {preparingOrders.length === 0 && !loading ? (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 4, textAlign: 'center', background: '#ffffff', color: '#475569', border: '1px dashed #cbd5e1' }}>
              <Typography variant="h6">Nenhum pedido no KDS agora</Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Assim que a operação enviar um pedido em preparo para o KDS, ele aparecerá aqui.
              </Typography>
            </Paper>
          </Grid>
        ) : (
          preparingOrders.map((order) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={order.orderId}>
              <Paper
                sx={{
                  p: 2,
                  height: '100%',
                  background: '#ffffff',
                  color: '#0f172a',
                  border: wasMarkedReadyByKds(order)
                    ? '2px solid #f97316'
                    : isSalonOrder(order)
                      ? '2px solid #dc2626'
                      : '1px solid #e2e8f0',
                  borderRadius: 3,
                  boxShadow: '0 10px 30px rgba(15,23,42,0.12)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{order.displayId || order.orderId}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {buildOrderChip(order)}
                    {shouldShowKdsRoute(order) ? (
                      <Chip label={getKdsRouteLabel(order)} color="warning" size="small" variant="filled" />
                    ) : null}
                    {isSalonOrder(order) && order.tableNumber ? (
                      <Chip label={`Mesa ${order.tableNumber}`} color="error" size="small" variant="outlined" />
                    ) : null}
                    <Chip label={statusLabels[order.status] || order.status} color="success" size="small" />
                  </Stack>
                </Box>
                <Typography variant="body2" sx={{ color: '#475569', mb: 1 }}>
                  {isSalonOrder(order)
                    ? `Mesa ${order.tableNumber || '-'}${order.waiterName ? ` • Garçom: ${order.waiterName}` : ''}`
                    : typeof order.customer === 'object' ? order.customer?.name : order.customer || 'Cliente'}
                </Typography>
                {isSalonOrder(order) && order.status === 'ready' ? (
                  <Box sx={{ bgcolor: '#dcfce7', border: '1px solid #16a34a', color: '#166534', borderRadius: 2, px: 1.5, py: 1, mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      ENTREGAR NA MESA {order.tableNumber || '-'}
                    </Typography>
                    <Typography variant="caption">
                      {order.waiterName ? `Garçom: ${order.waiterName}` : 'Pedido de salão'}
                      {order.tableRoundNumber ? ` • Rodada ${order.tableRoundNumber}` : ''}
                    </Typography>
                  </Box>
                ) : null}
                <Divider sx={{ mb: 1 }} />

                <Stack spacing={1.5}>
                  {(order.items || []).map((item, idx) => (
                    <Paper
                      key={idx}
                      sx={{
                        p: 1.5,
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 2,
                        display: 'flex',
                        gap: 1,
                      }}
                    >
                      {item.imageUrl && (
                        <Box
                          component="img"
                          src={resolveCatalogImageUrl(item.imageUrl)}
                          alt={item.name}
                          sx={{ width: 70, height: 70, borderRadius: 2, objectFit: 'cover', border: '1px solid rgba(148,163,184,0.2)', cursor: 'pointer' }}
                          onClick={() => setImagePreview({ open: true, url: item.imageUrl, title: item.name })}
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {item.quantity}x {item.name}
                          </Typography>
                        </Stack>
                        {getPreparationDescriptionLines(item.preparationDescription).length > 0 && (
                          <Box sx={{ mb: 0.5 }}>
                            {getPreparationDescriptionLines(item.preparationDescription).map((line, lineIndex) => (
                              <Typography
                                key={`${item.name}-prep-${lineIndex}`}
                                variant="caption"
                                component="div"
                                sx={{ color: '#475569', display: 'block' }}
                              >
                                {line}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {item.observations && (
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#b45309', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 0.5 }}>
                            Obs: {item.observations}
                          </Typography>
                        )}
                        {item.options && item.options.length > 0 && (
                          <Box>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>Complementos</Typography>
                            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                              {item.options.map((opt, optIdx) => (
                                <Box key={optIdx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  {opt.imageUrl && (
                                    <Box
                                      component="img"
                                      src={resolveCatalogImageUrl(opt.imageUrl)}
                                      alt={opt.name}
                                      sx={{ width: 40, height: 40, borderRadius: 1.5, objectFit: 'cover', border: '1px solid rgba(148,163,184,0.2)', cursor: 'pointer' }}
                                      onClick={() => setImagePreview({ open: true, url: opt.imageUrl, title: opt.name })}
                                    />
                                  )}
                                  <Box>
                                    <Typography variant="body2">{opt.quantity}x {opt.name}</Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Stack>
                {order.status === 'preparing' || order.kitchenAmendmentPending ? (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => changeOrderStatus(order.orderId, 'ready')}
                      disabled={!!markingReady[order.orderId]}
                    >
                      {markingReady[order.orderId] ? 'Enviando...' : order.kitchenAmendmentPending ? 'Adição pronta' : 'Pedido Pronto'}
                    </Button>
                  </Box>
                ) : null}
              </Paper>
            </Grid>
          ))
        )}
      </Grid>
      <Dialog
        open={imagePreview.open}
        onClose={() => setImagePreview({ open: false, url: '', title: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{imagePreview.title}</span>
          <IconButton onClick={() => setImagePreview({ open: false, url: '', title: '' })} size="small">
            ✕
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
          {imagePreview.url && (
            <Box
              component="img"
              src={resolveCatalogImageUrl(imagePreview.url)}
              alt={imagePreview.title}
              sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 2 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default KdsMonitor;
